import { BreakdownDimension, BreakdownRow, DesignStats } from './types';

// The counts each breakdown row carries, summed as they are.
const COUNTS = ['offered', 'viewed', 'accepted', 'declined', 'failed', 'swapped', 'added', 'swap_failed', 'swap_recovered', 'errors'] as const;

const plus = (a: number | null | undefined, b: number | null | undefined) => (a == null || b == null ? null : a + b);
const cents = (dollars: number) => Math.round(dollars * 100) / 100;

// Pure: the breakdown's finest rows summed per design and the `dimensions` picked, as the
// table's rows. A margin is unknown when any of its rows' is; the share of acceptances on
// actual freight is re-derived from the summed counts, as the API derives it per design.
export function sumBreakdown(rows: BreakdownRow[], dimensions: BreakdownDimension[]): DesignStats[] {
  const sums = new Map<string, DesignStats & { shipped: number | null }>();
  for (const row of rows) {
    const key = JSON.stringify([row.upsellId, row.designId, ...dimensions.map(dimension => row[dimension])]);
    const sum = sums.get(key);
    if (!sum) {
      sums.set(key, {
        upsellId: row.upsellId,
        designId: row.designId,
        ...Object.fromEntries(dimensions.map(dimension => [dimension, row[dimension]])),
        ...Object.fromEntries(COUNTS.map(count => [count, row[count] ?? 0])),
        revenue: row.revenue,
        margin: row.margin,
        marginEstimate: row.marginEstimate ?? null,
        shipped: row.shipped,
        firstSeen: row.firstSeen,
        lastSeen: row.lastSeen,
      } as DesignStats & { shipped: number | null });
      continue;
    }
    for (const count of COUNTS) sum[count] = (sum[count] ?? 0) + (row[count] ?? 0);
    sum.revenue += row.revenue;
    sum.margin = plus(sum.margin, row.margin);
    sum.marginEstimate = plus(sum.marginEstimate, row.marginEstimate);
    sum.shipped = plus(sum.shipped, row.shipped);
    if (row.firstSeen < sum.firstSeen) sum.firstSeen = row.firstSeen;
    if (row.lastSeen > sum.lastSeen) sum.lastSeen = row.lastSeen;
  }
  return [...sums.values()].map(({ shipped, ...sum }) => ({
    ...sum,
    revenue: cents(sum.revenue),
    margin: sum.margin == null ? null : cents(sum.margin),
    marginEstimate: sum.marginEstimate == null ? null : cents(sum.marginEstimate),
    marginShippedShare: shipped == null ? null : sum.accepted ? Math.min(1, shipped / sum.accepted) : 0,
  }));
}
